/**
 * The record screen (frame `65:2`, `Campanha · Dados da campanha`), denser than the frame at the
 * owner's request: the collection status in the page header (`./CollectionStatus`), the
 * candidature as a header with its TSE photo and the related-post share as a compact stat, then
 * Identificação, Aliados e adversários (`./Alliances`), Contas e links (`./Presence`), Período
 * and the retention block.
 *
 * §17.15: an `Analista` (no `control_plane.write`) gets read-only rows, with no edit action, no
 * save bar and no state control -- absence, not a disabled form. `ARCHIVED` is terminal
 * (platform-design §5.1.1): the same read-only rendering applies once a campaign reaches it,
 * whoever is looking, because no control here can move it back out.
 *
 * A linked candidature's Eleição reads the election the record embeds, so it renders with the
 * rest of the header. Only a candidature picked in this edit and not yet saved resolves its
 * election against the TSE registry the picker already loaded, and shows no photo until saved.
 */
import { Ellipsis } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { DurationInput, formatDuration } from '../../../chrome/form';
import { Absent } from '../../../chrome/measure/Absent';
import {
  DefinitionList,
  Page,
  PageHeader,
  PageSection,
  type Definition,
} from '../../../chrome/page';
import { ErrorState } from '../../../chrome/state';
import { QueryStates } from '../../../chrome/state/QueryStates';
import { Avatar, AvatarFallback, AvatarImage } from '../../../ui/avatar';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { Field, FieldDescription, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Textarea } from '../../../ui/textarea';
import { getLocale } from '../../../i18n';
import { useCan } from '../../session/orgContext';
import { TseCandidatePicker } from '../tse/TseCandidatePicker';
import { useElections } from '../tse/api/queries';
import { officeLabel, type LinkedCandidate } from '../tse/api/schemas';
import { tse } from '../tse/tseMessages';
import { AlliancesSection } from './Alliances';
import { CollectionStatus } from './CollectionStatus';
import { Presence } from './Presence';
import { RelevanceCard } from './RelevanceCard';
import { ScopeRow } from './ScopeRow';
import { useCampaignRecord, useUpdateCampaign } from './api/queries';
import {
  CAMPAIGN_TIMEZONES,
  MAX_RETENTION_SECONDS,
  MIN_RETENTION_SECONDS,
  MIN_UNRELATED_RETENTION_SECONDS,
  type Candidate,
  type CandidateDetail,
  type CampaignDetail,
  type ElectionSummary,
} from './api/schemas';
import { rm } from './recordMessages';
import { SaveBar } from './SaveBar';

const WRITE_CAPABILITY = 'control_plane.write';

function nullIfEmpty(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function toLinkedCandidate(candidate: Candidate | null): LinkedCandidate {
  if (candidate === null) return null;
  return {
    name: candidate.name,
    office: candidate.office,
    ballot_number: candidate.ballot_number,
    uf: candidate.uf,
    party: candidate.party,
    coalition: candidate.coalition,
    tse_election_id: candidate.tse_election_id,
    tse_registration_id: candidate.tse_registration_id,
  };
}

function zoneLabel(timezone: string): string {
  return CAMPAIGN_TIMEZONES.find((zone) => zone.value === timezone)?.label ?? timezone;
}

function stateLabel(state: CampaignDetail['state']): string {
  switch (state) {
    case 'ACTIVE':
      return rm('state.active');
    case 'PAUSED':
      return rm('state.paused');
    case 'ARCHIVED':
      return rm('state.archived');
  }
}

/** Only once the campaign has ended: before that, the date is a forecast its own end date or
 *  an archive can still move. */
function purgeRow(campaign: CampaignDetail): Definition[] {
  const today = new Date().toISOString().slice(0, 10);
  const ended =
    campaign.state === 'ARCHIVED' || (campaign.active_to !== null && campaign.active_to < today);
  if (!ended || campaign.data_purge_at === null) return [];
  const date = new Intl.DateTimeFormat(getLocale(), { dateStyle: 'long' }).format(
    new Date(campaign.data_purge_at),
  );
  return [{ term: rm('retention.purgeAt'), detail: date }];
}

/** A retention override: inherited from the organisation, or the campaign's own seconds
 *  (`null` while its number is not a whole number yet). */
type Override = 'inherit' | number | null;

interface RetentionValue {
  campaign: Override;
  unrelated: Override;
}

function retentionOf(campaign: CampaignDetail): RetentionValue {
  return {
    campaign: campaign.data_retention_seconds ?? 'inherit',
    unrelated: campaign.unrelated_media_retention_seconds ?? 'inherit',
  };
}

/** The update body's two keys; a custom period that is not a number yet sends -1 so `api`
 *  refuses it rather than reading it as "inherit". */
function retentionBody(value: RetentionValue): {
  data_retention_seconds: number | null;
  unrelated_media_retention_seconds: number | null;
} {
  const wire = (override: Override): number | null =>
    override === 'inherit' ? null : override === null ? -1 : override;
  return {
    data_retention_seconds: wire(value.campaign),
    unrelated_media_retention_seconds: wire(value.unrelated),
  };
}

function overrideDetail(override: number | null, inherited: number): string {
  return override === null
    ? rm('retention.inherit', { period: formatDuration(inherited) })
    : formatDuration(override);
}

function OverrideField(props: {
  label: string;
  inherited: number;
  minSeconds: number;
  value: Override;
  onChange: (value: Override) => void;
}): ReactNode {
  const modeId = useId();
  const periodId = useId();
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field>
        <FieldLabel htmlFor={modeId}>{props.label}</FieldLabel>
        <Select
          value={props.value === 'inherit' ? 'inherit' : 'custom'}
          onValueChange={(mode) => props.onChange(mode === 'inherit' ? 'inherit' : props.inherited)}
        >
          <SelectTrigger id={modeId} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="inherit">
              {rm('retention.inherit', { period: formatDuration(props.inherited) })}
            </SelectItem>
            <SelectItem value="custom">{rm('retention.custom')}</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      {props.value !== 'inherit' && (
        <Field>
          <FieldLabel htmlFor={periodId}>{rm('retention.period.label')}</FieldLabel>
          <DurationInput
            id={periodId}
            seconds={props.value}
            onChange={props.onChange}
            minSeconds={props.minSeconds}
            maxSeconds={MAX_RETENTION_SECONDS}
          />
        </Field>
      )}
    </div>
  );
}

function RetentionField(props: {
  campaign: CampaignDetail;
  value: RetentionValue;
  onChange: (value: RetentionValue) => void;
}): ReactNode {
  const { campaign, value } = props;
  return (
    <PageSection title={rm('retention.title')}>
      <div className="flex flex-col gap-4">
        <OverrideField
          label={rm('retention.label')}
          inherited={campaign.inherited_retention_seconds}
          minSeconds={MIN_RETENTION_SECONDS}
          value={value.campaign}
          onChange={(next) => props.onChange({ ...value, campaign: next })}
        />
        <FieldDescription>{rm('retention.description')}</FieldDescription>
        {purgeRow(campaign).length > 0 && <DefinitionList columns={1} items={purgeRow(campaign)} />}
        <OverrideField
          label={rm('retention.unrelated.label')}
          inherited={campaign.inherited_unrelated_media_retention_seconds}
          minSeconds={MIN_UNRELATED_RETENTION_SECONDS}
          value={value.unrelated}
          onChange={(next) => props.onChange({ ...value, unrelated: next })}
        />
        <FieldDescription>{rm('retention.unrelated.description')}</FieldDescription>
      </div>
    </PageSection>
  );
}

function electionLabel(election: ElectionSummary): string {
  return `${tse('election.cycle', { year: String(election.cycle_year) })} · ${tse(`scope.${election.scope}`)}`;
}

/** Nothing while the roster loads, so a pending fetch never reads as "no election"; a failed
 *  fetch is an error with a retry, never the empty-field marker. */
function ElectionDetail(props: { tenantId: string; electionId: string }): ReactNode {
  const elections = useElections(props.tenantId);
  if (elections.status === 'pending') return null;
  if (elections.status === 'error') {
    return (
      <ErrorState failure={elections.error.failure} onRetry={() => void elections.refetch()} />
    );
  }
  const election = elections.data.find((item) => item.id === props.electionId);
  return election !== undefined ? (
    electionLabel(election)
  ) : (
    <Absent reason={{ text: rm('field.empty') }} />
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')
  ).toUpperCase();
}

function Fact(props: { term: string; children: ReactNode; wide?: boolean }): ReactNode {
  return (
    <div className={props.wide ? 'col-span-2 min-w-0' : 'min-w-0'}>
      <dt className="text-caption text-muted-foreground">{props.term}</dt>
      <dd className="text-body-sm text-foreground">{props.children}</dd>
    </div>
  );
}

function empty(): ReactNode {
  return <Absent reason={{ text: rm('field.empty') }} />;
}

/** The candidature as a header: the TSE photo, the name and the facts in a dense grid, with
 *  removal in a menu. Only an empty, editable campaign shows the picker instead. */
function CandidaturaSection(props: {
  tenantId: string;
  campaignId: string;
  candidate: Candidate | null;
  /** The saved record's own election, which `candidate` may no longer be linked to mid-edit. */
  election: ElectionSummary | null;
  /** The saved candidature's photo; shown only while `candidate` is still that candidature. */
  saved: CandidateDetail | null;
  editable: boolean;
  onChange: (candidate: LinkedCandidate) => void;
  relevance: ReactNode;
}): ReactNode {
  const { candidate, election } = props;
  if (candidate === null) {
    return (
      <PageSection title={rm('candidate.title')}>
        {props.editable ? (
          <TseCandidatePicker
            tenantId={props.tenantId}
            value={candidate}
            onChange={props.onChange}
          />
        ) : (
          <p className="text-body-sm text-muted-foreground">{rm('candidate.empty')}</p>
        )}
      </PageSection>
    );
  }
  const linkedElectionId =
    candidate.tse_registration_id !== null ? candidate.tse_election_id : null;
  const photo =
    props.saved !== null &&
    props.saved.tse_registration_id !== null &&
    props.saved.tse_registration_id === candidate.tse_registration_id
      ? (props.saved.photo_url ?? null)
      : null;
  const subtitle = [candidate.office !== null ? officeLabel(candidate.office) : null, candidate.uf]
    .filter((part) => part !== null)
    .join(' · ');

  return (
    <PageSection card={false} title={rm('candidate.title')}>
      <div className="flex flex-col gap-4 rounded-lg border bg-card p-4 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 gap-4">
          <Avatar className="size-16 shrink-0 rounded-md sm:size-20">
            {photo !== null && (
              <AvatarImage
                src={photo}
                alt={rm('candidate.photo', { name: candidate.name })}
                className="object-cover"
              />
            )}
            <AvatarFallback className="rounded-md text-title-sm">
              {initialsOf(candidate.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <div className="flex min-w-0 items-start gap-2">
              <div className="flex min-w-0 flex-1 flex-col">
                <p className="truncate text-title-md">{candidate.name}</p>
                {subtitle !== '' && (
                  <p className="truncate text-body-sm text-muted-foreground">{subtitle}</p>
                )}
              </div>
              {linkedElectionId !== null && (
                <Badge variant="secondary" className="hidden shrink-0 sm:inline-flex">
                  {rm('candidate.tseBadge')}
                </Badge>
              )}
              {props.editable && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0"
                      aria-label={rm('candidate.more')}
                    >
                      <Ellipsis aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem variant="destructive" onSelect={() => props.onChange(null)}>
                      {rm('candidate.remove')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
              <Fact term={rm('candidate.number.label')}>
                {candidate.ballot_number !== null ? (
                  <span data-numeric>{candidate.ballot_number}</span>
                ) : (
                  empty()
                )}
              </Fact>
              <Fact term={rm('candidate.party.label')}>{candidate.party ?? empty()}</Fact>
              <Fact term={rm('candidate.office.label')}>
                {candidate.office !== null ? officeLabel(candidate.office) : empty()}
              </Fact>
              <Fact term={rm('candidate.state.label')}>{candidate.uf ?? empty()}</Fact>
              {linkedElectionId !== null && (
                <Fact term={rm('candidate.election.label')} wide>
                  {election !== null && election.id === linkedElectionId ? (
                    electionLabel(election)
                  ) : (
                    <ElectionDetail tenantId={props.tenantId} electionId={linkedElectionId} />
                  )}
                </Fact>
              )}
              <Fact term={rm('candidate.coalition.label')} wide>
                {candidate.coalition ?? empty()}
              </Fact>
              <Fact term={rm('candidate.scope.term')} wide>
                <ScopeRow campaignId={props.campaignId} />
              </Fact>
            </dl>
          </div>
        </div>
        <div className="border-t pt-3 lg:w-64 lg:shrink-0 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-4">
          {props.relevance}
        </div>
      </div>
    </PageSection>
  );
}

function relevanceOf(campaign: CampaignDetail): ReactNode {
  return (
    <RelevanceCard
      campaignId={campaign.id}
      share={campaign.related_post_share}
      related={campaign.related_post_share_related}
      total={campaign.related_post_share_total}
    />
  );
}

function ReadOnlyRecord(props: {
  tenantId: string;
  campaignId: string;
  campaign: CampaignDetail;
}): ReactNode {
  const { campaign } = props;
  return (
    <div className="flex flex-col gap-6">
      <CandidaturaSection
        tenantId={props.tenantId}
        campaignId={props.campaignId}
        candidate={campaign.candidate}
        election={campaign.candidate?.election ?? null}
        saved={campaign.candidate}
        editable={false}
        onChange={() => {
          /* read-only: the picker never mounts here */
        }}
        relevance={relevanceOf(campaign)}
      />
      <PageSection title={rm('section.identification')}>
        <DefinitionList
          columns={1}
          items={[
            { term: rm('name.label'), detail: campaign.name },
            {
              term: rm('description.label'),
              detail: campaign.description ?? <Absent reason={{ text: rm('field.empty') }} />,
            },
          ]}
        />
      </PageSection>
      <AlliancesSection campaignId={props.campaignId} editable={false} />
      <Presence campaignId={props.campaignId} editable={false} />
      <PageSection title={rm('section.period')}>
        <DefinitionList
          columns={3}
          items={[
            {
              term: rm('activeFrom.label'),
              detail: campaign.active_from ?? <Absent reason={{ text: rm('date.empty') }} />,
            },
            {
              term: rm('activeTo.label'),
              detail: campaign.active_to ?? <Absent reason={{ text: rm('date.empty') }} />,
            },
            { term: rm('timezone.label'), detail: zoneLabel(campaign.timezone) },
            { term: rm('state.label'), detail: stateLabel(campaign.state) },
          ]}
        />
      </PageSection>
      <PageSection title={rm('retention.title')}>
        <DefinitionList
          columns={1}
          items={[
            {
              term: rm('retention.label'),
              detail: overrideDetail(
                campaign.data_retention_seconds,
                campaign.inherited_retention_seconds,
              ),
            },
            ...purgeRow(campaign),
            {
              term: rm('retention.unrelated.label'),
              detail: overrideDetail(
                campaign.unrelated_media_retention_seconds,
                campaign.inherited_unrelated_media_retention_seconds,
              ),
            },
          ]}
        />
      </PageSection>
    </div>
  );
}

function EditableRecord(props: {
  campaignId: string;
  tenantId: string;
  campaign: CampaignDetail;
}): ReactNode {
  const { campaign } = props;
  const [name, setName] = useState(campaign.name);
  const [description, setDescription] = useState(campaign.description ?? '');
  const [timezone, setTimezone] = useState(campaign.timezone);
  const [activeFrom, setActiveFrom] = useState(campaign.active_from ?? '');
  const [activeTo, setActiveTo] = useState(campaign.active_to ?? '');
  const [candidate, setCandidate] = useState<LinkedCandidate>(
    toLinkedCandidate(campaign.candidate),
  );
  const savedRetention = retentionOf(campaign);
  const [retention, setRetention] = useState<RetentionValue>(savedRetention);

  const mutation = useUpdateCampaign(props.campaignId);
  const failure = mutation.error?.failure ?? null;

  const nameId = useId();
  const descriptionId = useId();
  const timezoneId = useId();
  const activeFromId = useId();
  const activeToId = useId();

  const changedCount = [
    name !== campaign.name,
    nullIfEmpty(description) !== (campaign.description ?? null),
    timezone !== campaign.timezone,
    (activeFrom === '' ? null : activeFrom) !== campaign.active_from,
    (activeTo === '' ? null : activeTo) !== campaign.active_to,
    JSON.stringify(candidate) !== JSON.stringify(toLinkedCandidate(campaign.candidate)),
    JSON.stringify(retention) !== JSON.stringify(savedRetention),
  ].filter(Boolean).length;

  function discard(): void {
    setName(campaign.name);
    setDescription(campaign.description ?? '');
    setTimezone(campaign.timezone);
    setActiveFrom(campaign.active_from ?? '');
    setActiveTo(campaign.active_to ?? '');
    setCandidate(toLinkedCandidate(campaign.candidate));
    setRetention(savedRetention);
  }

  function save(): void {
    mutation.mutate({
      name,
      description: nullIfEmpty(description),
      timezone,
      active_from: activeFrom === '' ? null : activeFrom,
      active_to: activeTo === '' ? null : activeTo,
      candidate,
      ...retentionBody(retention),
    });
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        <CandidaturaSection
          tenantId={props.tenantId}
          campaignId={props.campaignId}
          candidate={candidate}
          election={campaign.candidate?.election ?? null}
          saved={campaign.candidate}
          editable
          onChange={setCandidate}
          relevance={relevanceOf(campaign)}
        />

        <PageSection title={rm('section.identification')}>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-field-pair">
            <Field>
              <FieldLabel htmlFor={nameId}>{rm('name.label')}</FieldLabel>
              <Input id={nameId} value={name} onChange={(event) => setName(event.target.value)} />
            </Field>
            <Field>
              <FieldLabel htmlFor={descriptionId}>{rm('description.label')}</FieldLabel>
              <Textarea
                id={descriptionId}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Field>
          </div>
        </PageSection>

        <AlliancesSection campaignId={props.campaignId} editable />

        <Presence campaignId={props.campaignId} editable />

        <PageSection title={rm('section.period')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field>
              <FieldLabel htmlFor={activeFromId}>{rm('activeFrom.label')}</FieldLabel>
              <Input
                id={activeFromId}
                type="date"
                value={activeFrom}
                onChange={(event) => setActiveFrom(event.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={activeToId}>{rm('activeTo.label')}</FieldLabel>
              <Input
                id={activeToId}
                type="date"
                value={activeTo}
                onChange={(event) => setActiveTo(event.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={timezoneId}>{rm('timezone.label')}</FieldLabel>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger id={timezoneId} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_TIMEZONES.map((zone) => (
                    <SelectItem key={zone.value} value={zone.value}>
                      {zone.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </PageSection>

        <RetentionField campaign={campaign} value={retention} onChange={setRetention} />
      </div>

      <SaveBar
        changedCount={changedCount}
        pending={mutation.isPending}
        failure={failure}
        onSave={save}
        onDiscard={discard}
      />
    </>
  );
}

export function CampaignRecord(props: { campaignId: string; tenantId: string }): ReactNode {
  const query = useCampaignRecord(props.campaignId);
  const canWrite = useCan(WRITE_CAPABILITY);

  return (
    <QueryStates query={query} empty={null}>
      {(campaign) => {
        const editable = canWrite && campaign.state !== 'ARCHIVED';
        return (
          <Page>
            <PageHeader
              title={rm('section.details')}
              actions={<CollectionStatus campaign={campaign} canWrite={canWrite} />}
            />
            {editable ? (
              <EditableRecord
                campaignId={props.campaignId}
                tenantId={props.tenantId}
                campaign={campaign}
              />
            ) : (
              <ReadOnlyRecord
                tenantId={props.tenantId}
                campaignId={props.campaignId}
                campaign={campaign}
              />
            )}
          </Page>
        );
      }}
    </QueryStates>
  );
}
